import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Clock, MapPin, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Location, ChatMessage } from '../types';

interface AIChatbotProps {
  location: Pick<Location, 'location' | 'latitude' | 'longitude' | 'busyness' | 'vibeRating'> & Partial<Location>;
}

const AIChatbot: React.FC<AIChatbotProps> = ({ location }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestedQuestions = [
    `What's the best time to visit ${location.location}?`,
    `Where can I park near ${location.location}?`,
    `How busy is ${location.location} right now?`,
    `What are the peak hours at ${location.location}?`,
    `Any tips for avoiding crowds at ${location.location}?`,
  ];

  useEffect(() => {
    // Add welcome message
    const welcomeMessage: ChatMessage = {
      id: '1',
      type: 'ai',
      content: `Hi! I'm your VibeCheck AI assistant. I can help you with information about ${location.location}, including busyness patterns, parking options, and the best times to visit. What would you like to know?`,
      timestamp: new Date(),
    };
    setMessages([welcomeMessage]);
  }, [location]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = generateAIResponse(inputValue);
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: aiResponse,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const generateAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();

    if (input.includes('park') || input.includes('parking')) {
      return `For parking near ${location.location}, I recommend checking out the street parking on nearby roads. Based on community feedback, the best parking spots are usually available early in the morning. You can also use the Parking tab in this modal to see detailed parking recommendations with ratings.`;
    }

    if (input.includes('busy') || input.includes('crowd')) {
      return `Currently, ${location.location} is ${location.busyness}% busy. The best times to avoid crowds are early morning (6-8 AM) and late evening (after 8 PM). You can check the Busyness tab to see real-time patterns throughout the day.`;
    }

    if (input.includes('time') || input.includes('when')) {
      return `The best time to visit ${location.location} is early morning when it's least crowded. Based on community recommendations, aim for 6-8 AM for the best experience. You can also check the Community tab for more specific timing tips from other visitors.`;
    }

    if (input.includes('rating') || input.includes('vibe')) {
      return `${location.location} has a vibe rating of ${location.vibeRating}/5 stars! Community members love the atmosphere here. Check out the Community tab to read detailed reviews and recommendations from other visitors.`;
    }

    if (input.includes('directions') || input.includes('navigate')) {
      return `To get to ${location.location}, you can use the "Get Directions" button in the Overview tab, or I can help you find the best route. The location is at coordinates ${location.latitude}, ${location.longitude}. Would you like me to show you nearby parking options?`;
    }

    return `I'd be happy to help you with ${location.location}! You can ask me about parking, busyness patterns, best times to visit, or get directions. You can also explore the different tabs in this modal for detailed information.`;
  };

  const handleSuggestedQuestion = (question: string) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="ai-chatbot">
      {/* Suggested Questions */}
      <div className="suggested-questions">
        <h4>Quick Questions</h4>
        <div className="questions-grid">
          {suggestedQuestions.map((question, index) => (
            <button key={index} onClick={() => handleSuggestedQuestion(question)} className="suggested-question">
              {question}
            </button>
          ))}
        </div>
      </div>

      {/* Chat Messages */}
      <div className="chat-messages">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`message ${message.type}`}
            >
              <div className="message-avatar">{message.type === 'ai' ? <Bot size={16} /> : <User size={16} />}</div>
              <div className="message-content">
                <p>{message.content}</p>
                <span className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isTyping && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="message ai typing">
            <div className="message-avatar">
              <Bot size={16} />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="chat-input">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask me anything about this location..."
          className="chat-input-field"
        />
        <button onClick={handleSendMessage} disabled={!inputValue.trim()} className="send-button">
          <Send size={18} />
        </button>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <button
          className="quick-action"
          onClick={() => handleSuggestedQuestion(`Where can I park near ${location.location}?`)}
        >
          <MapPin size={16} />
          <span>Parking</span>
        </button>
        <button
          className="quick-action"
          onClick={() => handleSuggestedQuestion(`What's the best time to visit ${location.location}?`)}
        >
          <Clock size={16} />
          <span>Best Time</span>
        </button>
        <button
          className="quick-action"
          onClick={() => handleSuggestedQuestion(`How busy is ${location.location} right now?`)}
        >
          <Star size={16} />
          <span>Current Status</span>
        </button>
      </div>
    </div>
  );
};

export default AIChatbot;
